import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { jwtSelector } from "src/common/state/selectors/authSelector";
import { useNavigate } from "react-router-dom";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import { ButtonIcon } from "src/common/components/Button";
import { CREATE_OR_UPDATE_USER_AS_ADMIN_PERMISSIONS_LIST_COMPONENT } from "src/common/components/CreateOrUpdateUserAsAdmin/CreateOrUpdateUserAsAdminPermissionsList";
import { DeleteElementsSelectedFromList } from "src/common/components/DeleteElementsSelectedFromList/DeleteElementsSelectedFromList";
import { Checkbox, Dropdown } from "src/common/components/Input";
import TableComp from "src/common/components/Table/Table";
import CheckAll from "src/common/components/UsersTable/CheckAll";
import EmptyState from "src/features/statsNew/components/EmptyState";
import type { IAllowedItem } from "src/common/components/UsersTable/DisplayAllowedItems";
import { DisplayAllowedItems } from "src/common/components/UsersTable/DisplayAllowedItems";
import { UserCrudButtons } from "src/common/components/UsersTable/UserCrudButtons";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import {
  getConfirmationModalStateShow,
  openConfirmationModal,
} from "src/common/state/slice/modal/modalSlice";
import {
  getSearchIndex,
  fetchSearchIndex,
} from "src/features/projectGroups/state/projectGroupsSlice";
import RoundImagePreview from "src/features/projects/components/RoundImagePreview";
import {
  fetchUserDetails,
  getCreators,
  getTeamAsAdmin,
  setDeleteUsersDraft,
} from "src/features/team/state/teamSlice";
import { SearchAndSort } from "src/features/statsNew/components";

// Without having several const, given two concurrent API calls, there will be a race condition causing the component to render
// (incorrect datas) once the quickest API call returns. Here it happened with the "GET" permissions being quicker than the "PUT" permissions
// while sharing a single spinner.
export const COLLABORATORS_LIST_COMPONENT = "collaboratorsListComponent";
export const COLLABORATORS_TABLE_COMPONENT = "collaboratorsTableComponent";
export const ANOTHER_COLLABORATORS_TABLE_COMPONENT = "anotherCollaboratorsTableComponent";

const CollaboratorsList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tableContainerRef = useRef(null);

  const searchIndex = useAppSelector(getSearchIndex);
  const collaborators = useAppSelector(getCreators);
  const confirmationModalStateShow = useAppSelector(getConfirmationModalStateShow);
  const jwt = useAppSelector(jwtSelector);
  const [filteredCreators, setFilteredCreators] = useState(collaborators);

  // Process ProjectGroups from searchIndex for filter dropdown
  // NOTE: NO authType filtering - Collaborators view shows ALL ProjectGroups (none/simplified/standard)
  const projectGroupsForFilter = useMemo(() => {
    return searchIndex
      .filter((item) => item.type === "projectGroup")
      .map((pg) => ({
        id: pg.id,
        name: pg.name,
        authType: pg.authType, // Available for future use (AC#5)
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [searchIndex]);

  // Row-invariant lookups. These used to be rebuilt inside the per-collaborator
  // .map() — including a searchIndex.find per permission and a full
  // searchIndex.filter per project-group permission — so they re-ran on every
  // keystroke and every checkbox click.
  const projectGroupsById = useMemo(() => {
    const byId = new Map<number, typeof projectGroupsForFilter[number]>();
    projectGroupsForFilter.forEach((pg) => {
      if (!byId.has(pg.id)) byId.set(pg.id, pg);
    });
    return byId;
  }, [projectGroupsForFilter]);

  const projectsByIdFromIndex = useMemo(() => {
    const byId = new Map<number, typeof searchIndex[number]>();
    searchIndex.forEach((item) => {
      if (item.type === "project" && !byId.has(item.id)) byId.set(item.id, item);
    });
    return byId;
  }, [searchIndex]);

  const projectIdsByProjectGroupId = useMemo(() => {
    const byGroup = new Map<number, number[]>();
    searchIndex.forEach((item) => {
      if (item.type !== "project") return;
      const bucket = byGroup.get(item.projectGroupId);
      if (bucket) {
        bucket.push(item.id);
      } else {
        byGroup.set(item.projectGroupId, [item.id]);
      }
    });
    return byGroup;
  }, [searchIndex]);

  const [selectedProjectGroupId, setSelectedProjectGroupId] = useState("0");
  const [filterByText, setFilterByText] = useState("");
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isCheck, setIsCheck] = useState<Array<any>>([]);
  // We're computing table max height manually to avoid a lot of CSS headaches *PTSD images of Vietnam and failing scrollbars *
  const [tableHeight, setTableHeight] = useState(0);
  const [tableKey, setTableKey] = useState(0);

  // Useful to determine if any filter has been applied
  const maxRowsLengthRef = useRef(collaborators.length);

  const handleSelectAll = () => {
    if (isCheck.length > 0) {
      setIsCheck([]);
    } else {
      setIsCheck(filteredCreators.map((li) => String(li.id)));
    }
  };

  const handleClick = (e: any) => {
    const { id, checked } = e.target;

    // Save scroll position before state update
    if (tableContainerRef.current) {
      const current = tableContainerRef.current as HTMLDivElement;
      setScrollPosition(current.scrollTop);
    }

    // State update logic
    if (checked) {
      const nextValues = [...isCheck, id];
      setIsCheck(nextValues);
    } else {
      setIsCheck(isCheck.filter((item) => item !== id));
    }
  };

  useEffect(() => {
    // Don't make API calls until we have a JWT (axios headers get set when jwt is available)
    if (!jwt) {
      return;
    }

    void dispatch(fetchSearchIndex({}));
    void dispatch(getTeamAsAdmin({ componentId: COLLABORATORS_LIST_COMPONENT })).then(
      (res: any) => {
        setFilteredCreators(res.payload.data.creators);
        if (res.payload.data.creators.length > maxRowsLengthRef.current) {
          maxRowsLengthRef.current = res.payload.data.creators.length;
        }
      },
    );
  }, [dispatch, jwt]);

  useEffect(() => {
    const calculateHeight = () => {
      const divinaProportione = Math.round(window.innerHeight * (2 / 3));
      setTableHeight(divinaProportione);
    };

    calculateHeight();

    window.addEventListener("resize", calculateHeight);
    return () => {
      window.removeEventListener("resize", calculateHeight);
    };
  }, []);

  useEffect(() => {
    let filtered = collaborators;

    // Filter by project group
    if (selectedProjectGroupId !== "0") {
      filtered = filtered.filter((collaborator) => {
        return collaborator.permissions.find(
          (p) => String(p.project_group?.id) === String(selectedProjectGroupId),
        );
      });
    }

    // Filter by text (name, firstname, email)
    if (filterByText) {
      const search = filterByText.toLowerCase();
      filtered = filtered.filter(
        (collaborator) =>
          collaborator.email?.toLowerCase().includes(search) ||
          collaborator.firstname?.toLowerCase().includes(search) ||
          collaborator.lastname?.toLowerCase().includes(search),
      );
    }

    setFilteredCreators(filtered);
    if (filtered.length > maxRowsLengthRef.current) {
      maxRowsLengthRef.current = filtered.length;
    }
    setIsCheck([]);
  }, [selectedProjectGroupId, filterByText, collaborators.length]);

  // This avoids the problems caused by the "delete multiple" button staying accessible after the "delete multiple users confirmation modal" have been clicked
  useEffect(() => {
    if (!confirmationModalStateShow) {
      setIsCheck([]);
    }
  }, [confirmationModalStateShow]);

  // Update the state when the store was updated (typically, by an API call)
  useEffect(() => {
    setFilteredCreators(collaborators);
    setTableKey(tableKey + 1);
    setIsCheck([]);
    if (collaborators.length > maxRowsLengthRef.current) {
      maxRowsLengthRef.current = collaborators.length;
    }
  }, [collaborators]);

  // Use effect hook to set scroll position after state update
  useEffect(() => {
    if (tableContainerRef.current) {
      const current = tableContainerRef.current as HTMLDivElement;
      current.scrollTop = scrollPosition;
    }
  }, [isCheck]);

  const colHeaders = [
    "",
    t("general.firstname"),
    t("general.lastname"),
    t("general.email"),
    t("general.projectGroups"),
    t("general.actions"),
  ];

  // Set lookup: `isCheck.includes` ran once per row, i.e. O(rows * selected)
  // on every checkbox click.
  const isCheckSet = useMemo(() => new Set(isCheck), [isCheck]);

  const rows = filteredCreators.map((collaborator, i: number) => {
    // Fix for WIXAR-4562: Count unique PROJECT IDs, not project group IDs
    // "Accès à tous les parcours" means access to ALL project groups (keeping this display)
    // But we track project groups here for allowedItems display below
    const uniqueProjectIds = new Set<number>();
    const uniqueProjectGroupIds = new Set<number>();

    collaborator.permissions.forEach((p) => {
      if (p.project?.id) {
        // Permission is for a specific project
        // Find the project in searchIndex to get its project_group
        const projectInIndex = projectsByIdFromIndex.get(p.project.id);
        // Only count projects that belong to valid project groups in the organization
        if (
          projectInIndex?.projectGroupId &&
          projectGroupsById.has(projectInIndex.projectGroupId)
        ) {
          uniqueProjectIds.add(p.project.id);
          uniqueProjectGroupIds.add(projectInIndex.projectGroupId);
        }
      } else if (p.project_group?.id) {
        // Permission is for an entire project_group
        // Count all projects in this project_group
        if (projectGroupsById.has(p.project_group.id)) {
          (projectIdsByProjectGroupId.get(p.project_group.id) || []).forEach((projectId) => {
            uniqueProjectIds.add(projectId);
          });
          uniqueProjectGroupIds.add(p.project_group.id);
        }
      }
    });

    // Compare at project-group level: creators have PG-level permissions,
    // so "accès à tous les parcours" means access to ALL project groups
    const hasAccessToEverything = uniqueProjectGroupIds.size === projectGroupsForFilter.length;

    // Fix for WIXAR-4562: Build allowedItems from unique project groups via searchIndex
    const allowedItems: IAllowedItem[] = [];
    const projectGroupNamesAdded = new Set<string>();

    collaborator.permissions.forEach((p) => {
      if (p.project?.id) {
        // Permission is for a specific project
        // Find the project in searchIndex to get its project_group
        const projectInIndex = projectsByIdFromIndex.get(p.project.id);
        if (projectInIndex?.projectGroupId) {
          // Find the project group name from projectGroupsForFilter
          const projectGroup = projectGroupsById.get(projectInIndex.projectGroupId);
          if (projectGroup && !projectGroupNamesAdded.has(projectGroup.name)) {
            allowedItems.push({ name: projectGroup.name });
            projectGroupNamesAdded.add(projectGroup.name);
          }
        }
      } else if (p.project_group?.id) {
        // Permission is for an entire project_group
        const projectGroup = projectGroupsById.get(p.project_group.id);
        if (projectGroup && !projectGroupNamesAdded.has(projectGroup.name)) {
          allowedItems.push({ name: projectGroup.name });
          projectGroupNamesAdded.add(projectGroup.name);
        }
      }
    });

    return {
      checkbox: (
        <Checkbox
          checked={isCheckSet.has(String(collaborator.id))}
          key={`${i}-${collaborator.id}`}
          id={String(collaborator.id)}
          value={collaborator.id}
          onClick={(e) => {
            e.stopPropagation();
            handleClick(e);
          }}
        />
      ),
      avatar: <RoundImagePreview mediaId_OrIsItAnObjectLol={collaborator.source_avatar} />,
      firstName: collaborator.firstname,
      lastName: collaborator.lastname,
      email: collaborator.email,
      projectGroups: (
        <DisplayAllowedItems
          itemsAreProjectGroups
          hasAccessToEverything={hasAccessToEverything}
          allowedItems={allowedItems}
        />
      ),
      crudButtons: (
        <UserCrudButtons
          onClickEdit={() => {
            void dispatch(
              fetchUserDetails({
                ...collaborator,
                componentId: CREATE_OR_UPDATE_USER_AS_ADMIN_PERMISSIONS_LIST_COMPONENT,
              }),
            );
            navigate(`${collaborator.id}`);
          }}
          onClickDelete={() => {
            dispatch(
              setDeleteUsersDraft({
                type: "delete_creator",
                usersToBeDeleted: [{ id: collaborator.id, email: collaborator.email }],
              }),
            );
            dispatch(openConfirmationModal("confirm-delete-users"));
          }}
        />
      ),
    };
  });

  return (
    <>
      <div className="flex flex-col items-center w-full h-full overflow-y-auto personalize-scroll-visible">
        <div className="flex mt-4 w-[96%] justify-between items-end">
          <Dropdown
            id="collaborators-filter-projectgroup"
            label={`${t("general.projectGroup")} :`}
            options={projectGroupsForFilter.map((pg) => ({
              value: String(pg.id),
              optionText: pg.name,
            }))}
            onChangeCb={(e: any) => {
              setSelectedProjectGroupId(e);
            }}
          />
          {/* Hidden mirror dropdown for backward-compatible test selectors */}
          <select
            id={`dropdown-${t("general.projectGroup")} :`}
            style={{
              position: "absolute",
              opacity: 0,
              pointerEvents: "none",
              height: 0,
              overflow: "hidden",
            }}
            value={selectedProjectGroupId}
            onChange={(e) => {
              setSelectedProjectGroupId(e.target.value);
            }}
          >
            <option value="0">{t("general.pleaseSelect")}</option>
            {projectGroupsForFilter.map((pg) => (
              <option key={pg.id} value={String(pg.id)}>
                {pg.name}
              </option>
            ))}
          </select>
          <SearchAndSort
            searchValue={filterByText}
            onSearchChange={(value) => {
              setIsCheck([]);
              setFilterByText(value);
            }}
            searchPlaceholder={t("pages.collaborators.searchPlaceholder")}
            searchWidth="min-w-[320px]"
          />
          <ButtonIcon
            icon="PlusIcon"
            styled="btn-primary-fill h-2/3"
            text={t("pages.collaborators.addNewCollaborator")}
            onClick={() => navigate("/team/new")}
          />
        </div>
        <DeleteElementsSelectedFromList
          className="self-start ml-12 mt-2"
          isCheck={isCheck}
          selectedTextSingular={t("pages.participants.selectedUserSingular")}
          selectedTextPlurial={t("pages.participants.selectedUserPlurial")}
          deleteButtonTextSingular={t("pages.participants.deleteSelectedUserSingular")}
          deleteButtonTextPlurial={t("pages.participants.deleteSelectedUserPlural")}
          deleteCallback={() => {
            dispatch(
              setDeleteUsersDraft({
                type: isCheck.length > 1 ? "delete_creators" : "delete_creator",
                usersToBeDeleted: collaborators.filter((c) => isCheck.includes(String(c.id))),
              }),
            );
            dispatch(openConfirmationModal("confirm-delete-users"));
          }}
        />
        <IsLoading componentId={COLLABORATORS_LIST_COMPONENT} showSuccess={false}>
          <IsLoading componentId={COLLABORATORS_TABLE_COMPONENT} showSuccess={false}>
            <IsLoading componentId={ANOTHER_COLLABORATORS_TABLE_COMPONENT} showSuccess={false}>
              <TableComp
                key={`${tableHeight}${tableKey}`}
                style={{ maxHeight: `${tableHeight}px` }} // Long story short, Tailwind doesn't do well with dynamic class values
                className="w-[96%] mt-2 mb-2"
                colHeaders={colHeaders}
                nonSortableColumns={[t("general.actions")]}
                rows={rows}
                disableCriteria={() => false}
                isAllCollaborators={true}
                isCollaboratorAndParticipant={true}
                setListOfItemsFilter={setFilteredCreators}
                listTofilter={filteredCreators}
                checkAll={
                  <CheckAll
                    className={filteredCreators.length === 0 ? "hidden" : ""}
                    checked={isCheck.length > 0}
                    onChange={handleSelectAll}
                  />
                }
                isUserList={true}
                selectFromListInTable={true}
                emptyPlaceholder={
                  maxRowsLengthRef.current ? (
                    <EmptyState
                      icon="search"
                      title={t("statsNew.emptyState.noSearchResults")}
                      description={t("statsNew.emptyState.noSearchResultsDescription")}
                    />
                  ) : (
                    <div className="flex m-8 items-center justify-center">
                      {t("pages.collaborators.noCollaboratorCTA")}
                    </div>
                  )
                }
              />
            </IsLoading>
          </IsLoading>
        </IsLoading>
      </div>
    </>
  );
};

export default CollaboratorsList;
